/**
 * ConfidentialTab — Phase 4 Sub-Pass 3.
 *
 * Renders EEO Self-ID, I-9 Work Authorization, WOTC, and Sensitive HR sub-tabs.
 * The parent <EmployeeDetail/> only mounts this when the caller has the
 * `confidential` HR grant (see EmployeeDetail.jsx canSeeConfidentialTab).
 *
 * These fields are segregated from assignment/scheduling/evaluation by design —
 * they live on the employee record but are presented behind the confidential
 * wall so operational users never see them.
 *
 * Editing uses PUT /api/employees/{id} (employees:edit cap + confidential grant
 * are enforced server-side).
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Save, Pencil, X, ShieldCheck, Users, Briefcase, Crosshair } from 'lucide-react';
import { toast } from 'sonner';
import { employeesAPI } from '../../lib/api';
import { extractErrorMessage } from '../../lib/errors';
import RequireCap from '../../components/auth/RequireCap';

const ETHNICITY_OPTIONS = [
  'White (Not Hispanic or Latino)',
  'Black or African American (Not Hispanic or Latino)',
  'Hispanic or Latino',
  'Asian (Not Hispanic or Latino)',
  'American Indian or Alaska Native (Not Hispanic or Latino)',
  'Native Hawaiian or Other Pacific Islander (Not Hispanic or Latino)',
  'Two or More Races (Not Hispanic or Latino)',
  'Other',
  'Prefer not to answer',
];
const CITIZENSHIP_OPTIONS = [
  { value: '1', label: '1 — A citizen of the United States' },
  { value: '2', label: '2 — A noncitizen national of the United States' },
  { value: '3', label: '3 — A lawful permanent resident' },
  { value: '4', label: '4 — An alien authorized to work' },
];
const SEX_OPTIONS = ['Male', 'Female', 'Non-Binary', 'Prefer not to answer'];

// ─────────────────────────────────────────────────────────────────────────
// Generic confidential-field section with view/edit toggle
// ─────────────────────────────────────────────────────────────────────────
function SectionForm({ employee, employeeId, fields, onSaved, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    const d = {};
    fields.forEach(f => { d[f.name] = employee?.[f.name] ?? (f.type === 'bool' ? false : ''); });
    setDraft(d);
    setEditing(true);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      // PUT /api/employees/{id} requires the full EmployeeCreate payload (Pydantic
      // would otherwise 422 on missing required fields like first_name/last_name).
      // Merge the current employee record with the edited draft, then strip the
      // server-managed and UI-only keys so the body matches EmployeeCreate exactly.
      const merged = { ...(employee || {}), ...draft };
      [
        'id', '_id', 'created_at', 'updated_at',
        'ssn_last_4', 'ssn_encrypted', 'ssn_plaintext',
        '_classification_other_active', '_post_type_other_active', '_pay_dist_other_active',
      ].forEach(k => { delete merged[k]; });
      // confidential grant is implicit (the tab itself is grant-gated); employees:edit
      // is still enforced server-side.
      await employeesAPI.update(employeeId, merged);
      toast.success('Saved');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      // extractErrorMessage handles FastAPI 422 arrays, HTTPException strings, and
      // network errors so the toast never receives an object/array (which would
      // otherwise crash React: "Objects are not valid as a React child").
      toast.error(extractErrorMessage(e, 'Failed to save'));
    } finally { setSaving(false); }
  };

  const render = (f, value) => {
    if (f.type === 'bool') return value === true ? 'Yes' : value === false ? 'No' : '—';
    if (f.type === 'select') {
      const opt = f.options.find(o => (typeof o === 'string' ? o : o.value) === value);
      return opt ? (typeof opt === 'string' ? opt : opt.label) : (value || '—');
    }
    return value || '—';
  };

  const inputFor = (f) => {
    const v = draft[f.name];
    if (f.type === 'bool') {
      return (
        <div className="flex items-center gap-2 pt-1">
          <input type="checkbox" id={`${testId}-${f.name}`} checked={!!v} onChange={e => setDraft({ ...draft, [f.name]: e.target.checked })} />
          <Label htmlFor={`${testId}-${f.name}`} className="font-normal text-sm">{f.label}</Label>
        </div>
      );
    }
    if (f.type === 'select') {
      return (
        <Select value={v || ''} onValueChange={x => setDraft({ ...draft, [f.name]: x })}>
          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {f.options.map(o => {
              const val = typeof o === 'string' ? o : o.value;
              const lbl = typeof o === 'string' ? o : o.label;
              return <SelectItem key={val} value={val}>{lbl}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      );
    }
    return <Input value={v || ''} onChange={e => setDraft({ ...draft, [f.name]: e.target.value })} />;
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-end mb-3 gap-2">
        {!editing ? (
          <RequireCap module="employees" action="edit">
            <Button size="sm" variant="outline" onClick={startEdit} data-testid={`${testId}-edit`}><Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
          </RequireCap>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}><X className="w-3.5 h-3.5" /></Button>
            <Button size="sm" onClick={onSave} disabled={saving} data-testid={`${testId}-save`}><Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving…' : 'Save'}</Button>
          </>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map(f => (
          <div key={f.name} className={f.fullWidth ? 'md:col-span-2' : ''}>
            {f.type !== 'bool' || !editing ? <Label>{f.label}</Label> : null}
            {editing ? inputFor(f) : (
              <p className="font-medium pt-1 text-sm" data-testid={`${testId}-${f.name}-value`}>
                {render(f, employee?.[f.name])}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// EEO SELF-ID SUB-TAB
// ─────────────────────────────────────────────────────────────────────────
function EEOSubtab({ employee, employeeId, onSaved }) {
  const fields = [
    { name: 'ethnicity_race', label: 'Race / Ethnicity', type: 'select', options: ETHNICITY_OPTIONS },
    { name: 'ethnicity_other', label: 'If "Other," specify' },
    { name: 'sex', label: 'Sex', type: 'select', options: SEX_OPTIONS },
    { name: 'disability', label: 'Disability', type: 'bool' },
    { name: 'veteran_us_armed_forces', label: 'U.S. Armed Forces veteran', type: 'bool' },
    { name: 'eligible_to_work_us', label: 'Eligible to work in the U.S.', type: 'bool' },
  ];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Users className="w-5 h-5 text-emerald-600" /> EEO Self-Identification</CardTitle>
        <CardDescription>Voluntary self-identification responses. Never used for assignment, scheduling, or evaluation.</CardDescription>
      </CardHeader>
      <CardContent>
        <SectionForm employee={employee} employeeId={employeeId} fields={fields} onSaved={onSaved} testId="conf-eeo" />
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// I-9 WORK AUTH SUB-TAB
// ─────────────────────────────────────────────────────────────────────────
function I9Subtab({ employee, employeeId, onSaved }) {
  const fields = [
    { name: 'citizenship_status', label: 'Citizenship / Immigration Status', type: 'select', options: CITIZENSHIP_OPTIONS, fullWidth: true },
    { name: 'used_other_last_names', label: 'Used other last names (status only)', type: 'bool' },
  ];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Briefcase className="w-5 h-5 text-blue-600" /> Work Authorization (I-9)</CardTitle>
        <CardDescription>USCIS Form I-9 attestation status. Documents themselves are stored under Attachments — this tab tracks the status only.</CardDescription>
      </CardHeader>
      <CardContent>
        <SectionForm employee={employee} employeeId={employeeId} fields={fields} onSaved={onSaved} testId="conf-i9" />
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// WOTC SUB-TAB
// ─────────────────────────────────────────────────────────────────────────
function WOTCSubtab({ employee, employeeId, onSaved }) {
  const fields = [
    { name: 'wotc_occupational_category', label: 'Occupational Category' },
    { name: 'wotc_snap_family', label: 'SNAP family in last 6 months', type: 'bool' },
    { name: 'wotc_iv_a_tanf', label: 'IV-A TANF family in last 18 months', type: 'bool' },
    { name: 'wotc_ssi_60_days', label: 'SSI in last 60 days', type: 'bool' },
    { name: 'wotc_veteran', label: 'Qualifying veteran', type: 'bool' },
    { name: 'wotc_felon_released', label: 'Felony conviction in last year', type: 'bool' },
    { name: 'wotc_long_term_unemployed', label: 'Long-term unemployed', type: 'bool' },
    { name: 'wotc_voc_rehab', label: 'Vocational rehab referral', type: 'bool' },
  ];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Crosshair className="w-5 h-5 text-amber-600" /> WOTC Target-Group Responses</CardTitle>
        <CardDescription>Work Opportunity Tax Credit — stored responses only. No tax-credit compute happens here.</CardDescription>
      </CardHeader>
      <CardContent>
        <SectionForm employee={employee} employeeId={employeeId} fields={fields} onSaved={onSaved} testId="conf-wotc" />
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SENSITIVE SUB-TAB
// ─────────────────────────────────────────────────────────────────────────
function SensitiveSubtab({ employee, employeeId, onSaved }) {
  const fields = [
    { name: 'active_or_retired_le', label: 'Active or retired law enforcement', type: 'bool' },
    { name: 'le_agency', label: 'LE agency (if applicable)' },
    { name: 'active_duty_military', label: 'Active-duty military', type: 'bool' },
    { name: 'weapon_brand', label: 'Issued weapon — brand' },
    { name: 'weapon_model', label: 'Weapon model' },
    { name: 'weapon_caliber', label: 'Weapon caliber' },
    { name: 'weapon_serial_number', label: 'Weapon serial #' },
    { name: 'weapon_notes', label: 'Weapon notes', fullWidth: true },
    { name: 'next_of_kin_employer', label: 'Next-of-kin employer' },
    { name: 'religious_preference', label: 'Religious preference (voluntary)' },
    { name: 'marital_status', label: 'Marital status (voluntary)' },
  ];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-rose-600" /> Sensitive HR Fields</CardTitle>
        <CardDescription>LE history, active-duty military, issued-weapon details, next-of-kin, religious preference. Treated as confidential — never surfaced operationally.</CardDescription>
      </CardHeader>
      <CardContent>
        <SectionForm employee={employee} employeeId={employeeId} fields={fields} onSaved={onSaved} testId="conf-sensitive" />
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PARENT — CONFIDENTIAL TAB
// ─────────────────────────────────────────────────────────────────────────
export default function ConfidentialTab({ employee, employeeId, onSaved }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-amber-600" /> Confidential / HR
          <Badge variant="outline" className="text-xs border-amber-300 text-amber-900 ml-2">confidential grant required</Badge>
        </CardTitle>
        <CardDescription>
          EEO Self-Identification, I-9 Work Authorization, WOTC responses, and Sensitive
          HR fields. Segregated by design — these data are never used for assignment,
          scheduling, or evaluation decisions.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="eeo" className="space-y-4">
          <TabsList className="flex flex-wrap">
            <TabsTrigger value="eeo" data-testid="confidential-subtab-eeo"><Users className="w-4 h-4 mr-1.5" />EEO Self-ID</TabsTrigger>
            <TabsTrigger value="i9" data-testid="confidential-subtab-i9"><Briefcase className="w-4 h-4 mr-1.5" />I-9 / Work Auth</TabsTrigger>
            <TabsTrigger value="wotc" data-testid="confidential-subtab-wotc"><Crosshair className="w-4 h-4 mr-1.5" />WOTC</TabsTrigger>
            <TabsTrigger value="sensitive" data-testid="confidential-subtab-sensitive"><ShieldCheck className="w-4 h-4 mr-1.5" />Sensitive</TabsTrigger>
          </TabsList>
          <TabsContent value="eeo"><EEOSubtab employee={employee} employeeId={employeeId} onSaved={onSaved} /></TabsContent>
          <TabsContent value="i9"><I9Subtab employee={employee} employeeId={employeeId} onSaved={onSaved} /></TabsContent>
          <TabsContent value="wotc"><WOTCSubtab employee={employee} employeeId={employeeId} onSaved={onSaved} /></TabsContent>
          <TabsContent value="sensitive"><SensitiveSubtab employee={employee} employeeId={employeeId} onSaved={onSaved} /></TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
